/**
 * ActivitySidebar - Recent family activity feed with rollup
 *
 * Shows coalesced activity entries ("Alice uploaded 5 photos").
 * Clicking a rolled-up entry navigates to a filtered timeline.
 * Comment rollups with multiple targets open a detail modal.
 * Desktop only (hidden on mobile). Auto-refreshes every 30 seconds.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useActivities,
  formatRelativeTime,
  getActivityText,
  type ActivityTarget,
  type ActivityItem,
} from '@wovendays/api-client';
import { useTranslation } from '@wovendays/i18n';
import { CommentActivityModal } from './CommentActivityModal';

/** Build a thumbnail URL from signed gridUrl */
function getThumbnailUrl(target: ActivityTarget): string | null {
  if (target.gridUrl) return target.gridUrl;
  return null;
}

interface ActivitySidebarProps {
  familyId: string;
}

export function ActivitySidebar({ familyId }: ActivitySidebarProps) {
  const { t } = useTranslation('activity');
  const { data, isLoading } = useActivities(familyId);
  const [commentActivity, setCommentActivity] = useState<ActivityItem | null>(null);

  const activities = data?.activities ?? [];

  return (
    <div aria-label={t('title')}>
      <h3 className="mb-3 font-mono text-xs uppercase tracking-widest text-muted-foreground">
        {t('title')}
      </h3>
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : activities.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('empty')}</p>
      ) : (
        <div className="space-y-1">
          {activities.map((activity) => {
            // For rolled-up photo uploads, link to filtered timeline
            const isRolledUpPhotos = activity.type === 'photo_uploaded' && activity.count > 1 && activity.targetIds.length > 0;
            // Comment rollups with multiple targets open the modal
            const isCommentRollup = activity.type === 'comment_added' && activity.targetIds.length > 1;
            const linkTarget = isRolledUpPhotos
              ? `/families/${familyId}/timeline?mediaIds=${activity.targetIds.join(',')}`
              : !isCommentRollup && activity.targetIds.length === 1
                ? `/photo/${activity.targetIds[0]}`
                : undefined;

            // Show stacked thumbnails for rolled-up entries
            const previewTargets = activity.targets.slice(0, 3);
            const thumbnailUrl = previewTargets.length > 0 ? getThumbnailUrl(previewTargets[0]!) : null;

            const inner = (
              <div className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-muted/50">
                {isRolledUpPhotos && previewTargets.length > 1 ? (
                  <div className="relative h-9 w-9 shrink-0">
                    {previewTargets.slice(0, 3).map((target, i) => {
                      const url = getThumbnailUrl(target);
                      return url ? (
                        <img
                          key={target.id}
                          src={url}
                          alt=""
                          className="absolute h-7 w-7 rounded-md object-cover border border-background"
                          style={{
                            top: i * 2,
                            left: i * 3,
                            zIndex: 3 - i,
                          }}
                          decoding="async"
                          loading="lazy"
                        />
                      ) : null;
                    })}
                  </div>
                ) : thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt=""
                    className="h-9 w-9 shrink-0 rounded-md object-cover"
                    decoding="async"
                    loading="lazy"
                  />
                ) : (
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-xs text-muted-foreground">
                    {activity.type === 'member_joined' ? '👋' : activity.type === 'photo_loved' ? '❤️' : '📷'}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground">
                    <span className="font-medium">{activity.userName}</span>{' '}
                    {getActivityText(activity)}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {formatRelativeTime(activity.createdAt)}
                  </p>
                </div>
              </div>
            );

            if (linkTarget) {
              return (
                <Link key={activity.id} to={linkTarget}>
                  {inner}
                </Link>
              );
            }
            if (isCommentRollup) {
              return (
                <button
                  key={activity.id}
                  onClick={() => setCommentActivity(activity)}
                  className="w-full text-left cursor-pointer"
                >
                  {inner}
                </button>
              );
            }
            return <div key={activity.id}>{inner}</div>;
          })}
        </div>
      )}
      <CommentActivityModal
        activity={commentActivity}
        open={!!commentActivity}
        onClose={() => setCommentActivity(null)}
      />
    </div>
  );
}
